import { computed, type Ref } from "vue";
import { useHead } from "#imports";
// Assuming you have the same utils available
import { removeHtmlTagFromString } from "~~/utils";

export const useCompanySchema = (companyItem: Ref<unknown>, locale: Ref<string>) => {
	const jsonLdSchema = computed(() => {
		if (!companyItem.value) return null;

		const company = companyItem.value;
		const currentLocale = locale.value;

		// 1. Extract a description (Prioritize Mission, fallback to Product description)
		let description = "";
		if (company.boxes?.mission?.data?.mission?.[currentLocale]) {
			description = removeHtmlTagFromString(company.boxes.mission.data.mission[currentLocale]);
		} else if (company.boxes?.product?.data?.product?.description?.[currentLocale]) {
			description = removeHtmlTagFromString(company.boxes.product.data.product.description[currentLocale]);
		} else if (company.boxes?.product?.data?.service?.description?.[currentLocale]) {
			description = removeHtmlTagFromString(company.boxes.product.data.service.description[currentLocale]);
		} else if (company.boxes?.product?.data?.description?.[currentLocale]) {
			description = removeHtmlTagFromString(company.boxes.product.data.description[currentLocale]);
		}

		// 2. Gather Social Media Links for the "sameAs" property
		const sameAsLinks = [];
		if (company.follow_us?.facebook) sameAsLinks.push(company.follow_us.facebook);
		if (company.follow_us?.linkedin) sameAsLinks.push(company.follow_us.linkedin);

		// 3. Extract the HQ Address
		let addressObj = undefined;
		const addressData = company.boxes?.address?.data?.[0]; // Taking the first address
		if (addressData) {
			addressObj = {
				"@type": "PostalAddress",
				streetAddress: addressData.formatted_address?.split(",")[0] || "",
				addressLocality: addressData.locality || "",
				postalCode: addressData.postal_code || "",
				addressCountry: addressData.country_code || (company.region === "RO" ? "RO" : "HU"),
			};
		}

		// 4. Extract Founding Date (Grabbing the earliest date from history box)
		let foundingDate = undefined;
		const historyData = company.boxes?.history?.data?.[currentLocale];
		if (historyData && historyData.length > 0) {
			// Assuming the first item is the oldest/founding date
			foundingDate = historyData[0].date;
		}

		// Build the Schema object
		const schema: Record<string, unknown> = {
			"@context": "https://schema.org",
			"@type": "Organization", // You can also use "Corporation" here
			name: company.name,
			url:
				company.follow_us?.web_address ||
				`https://${globalConfig.host}/${locale.value}/company/${company.slug}`,
			logo: company.logo?.image || undefined,
			description: description || undefined,
			foundingDate: foundingDate || undefined,
			sameAs: sameAsLinks.length > 0 ? sameAsLinks : undefined,
			address: addressObj,
		};

		// Clean up undefined properties to keep the JSON payload tidy
		Object.keys(schema).forEach((key) => schema[key] === undefined && delete schema[key]);

		return schema;
	});

	// Inject into the <head>
	useHead(() => ({
		script: [
			{
				type: "application/ld+json",
				innerHTML: jsonLdSchema.value ? JSON.stringify(jsonLdSchema.value) : undefined,
			},
		],
	}));
};
